class CollectionInfiniteScroll extends HTMLElement {
  connectedCallback() {
    this.nextUrl = this.dataset.nextUrl;
    this.sectionId = this.dataset.sectionId;
    this.mode = this.dataset.mode;
    this.sentinel = this.querySelector('[data-infinite-scroll-sentinel]');
    this.loadButton = this.querySelector('[data-infinite-scroll-button]');
    this.liveRegion = this.querySelector('[data-infinite-scroll-live-region]');
    this.loading = false;

    if (!this.nextUrl || !this.sectionId || !['show_more', 'infinite_scroll'].includes(this.mode)) return;
    if (this.mode === 'show_more' && !this.loadButton) return;
    if (this.mode === 'infinite_scroll' && (!this.sentinel || !('IntersectionObserver' in window))) return;

    this.classList.add('is-active');
    if (this.mode === 'show_more') {
      this.handleButtonClick = () => this.loadNextPage();
      this.loadButton.addEventListener('click', this.handleButtonClick);
    } else if (this.mode === 'infinite_scroll') {
      this.observer = new IntersectionObserver(this.handleIntersection.bind(this), {
        rootMargin: '600px 0px',
      });
      this.observer.observe(this.sentinel);
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.abortController?.abort();
    this.loadButton?.removeEventListener('click', this.handleButtonClick);
  }

  handleIntersection(entries) {
    if (!entries.some((entry) => entry.isIntersecting) || this.loading || !this.nextUrl) return;
    this.loadNextPage();
  }

  async loadNextPage() {
    if (this.loading || !this.nextUrl) return;

    const loadedPageUrl = this.nextUrl;
    this.loading = true;
    this.classList.add('is-active');
    this.classList.add('is-loading');
    this.classList.remove('has-error');
    if (this.loadButton) this.loadButton.disabled = true;
    if (this.liveRegion) this.liveRegion.textContent = this.dataset.loadingMessage;
    this.observer?.disconnect();
    this.abortController = new AbortController();

    try {
      const requestUrl = new URL(this.nextUrl, window.location.origin);
      requestUrl.searchParams.set('section_id', this.sectionId);

      const response = await fetch(requestUrl.toString(), {
        credentials: 'same-origin',
        signal: this.abortController.signal,
      });
      if (!response.ok) throw new Error(`Infinite scroll request failed with status ${response.status}`);

      const responseDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
      const incomingGrid = responseDocument.getElementById('product-grid');
      const currentGrid = document.getElementById('product-grid');
      const nextLoader = responseDocument.querySelector('collection-infinite-scroll');

      if (!incomingGrid || !currentGrid) throw new Error('Infinite scroll response did not contain a product grid');

      const newItems = Array.from(incomingGrid.children);
      const incomingNextUrl = nextLoader?.dataset.nextUrl || '';
      if (!newItems.length) throw new Error('Infinite scroll response did not contain any products');
      if (
        incomingNextUrl &&
        new URL(incomingNextUrl, window.location.origin).href === new URL(loadedPageUrl, window.location.origin).href
      ) {
        throw new Error('Infinite scroll response repeated the current page');
      }

      currentGrid.append(...newItems);
      this.nextUrl = incomingNextUrl;
      this.dataset.nextUrl = this.nextUrl;

      const currentPagination = this.querySelector('.pagination-wrapper');
      const incomingPagination = nextLoader?.querySelector('.pagination-wrapper');
      if (currentPagination && incomingPagination) currentPagination.replaceWith(incomingPagination);

      if (this.liveRegion) this.liveRegion.textContent = nextLoader?.dataset.productsMessage || '';

      if (!this.nextUrl) {
        this.classList.add('is-complete');
        this.observer?.disconnect();
        this.sentinel?.remove();
      }
    } catch (error) {
      if (error.name === 'AbortError') return;

      console.error(error);
      this.classList.add('has-error');
      this.classList.remove('is-active');
      if (this.liveRegion) this.liveRegion.textContent = this.dataset.errorMessage;
      return;
    } finally {
      this.loading = false;
      if (this.loadButton) this.loadButton.disabled = false;
      this.classList.remove('is-loading');
    }

    if (this.mode === 'infinite_scroll' && this.nextUrl && this.isConnected) this.observer.observe(this.sentinel);
  }
}

if (!customElements.get('collection-infinite-scroll')) {
  customElements.define('collection-infinite-scroll', CollectionInfiniteScroll);
}
